import { gql } from '@apollo/client';

import apolloClient, { queryBuilder } from '.';
import { transformGraphQLResponse } from './utils';
import { fieldMetricMapping } from '../components/Table/utils';
import analytics from '../helper/analytics';
import tracker from '../constants/tracker';

/**
 * Docs
 * https://www.ag-grid.com/react-data-grid/server-side-operations-graphql/
 */
class ServerSideDataSource {
	constructor(gridOptions, queryParams) {
		this.client = apolloClient;
		this.gridOptions = gridOptions;
		this.queryParams = queryParams;
	}

	// Setters
	setGridOptions(gridOptions) {
		this.gridOptions = gridOptions;
	}

	setQueryParams(queryParams) {
		this.queryParams = queryParams;
	}

	// Getters
	getRows(params) {
		let additionalParams = {};
		// If the parent Node has data (accordion opens in the table)
		// params id & view level will be passed to the getQuery to build the query
		if (params?.parentNode?.data) {
			const data = { ...params?.parentNode?.data };
			additionalParams.id = data?.id;
			additionalParams.name = data?.name.toUpperCase();
			additionalParams.viewBy = data?.level;
			additionalParams.rootParam = data?.rootParam;
			additionalParams.type = data?.type;
			additionalParams.standardName = data?.standardName;
		}
		const defaultSort = { sort: 'desc', colId: 'ty_ns' };
		const { sort, colId } = params?.request?.sortModel?.length
			? params.request.sortModel[0]
			: defaultSort;
		const colIdKey = colId?.split('_')?.slice(-2)?.join('_');
		const sortBy = fieldMetricMapping[colIdKey];
		additionalParams.sortOrder = String(sort).toUpperCase();
		additionalParams.sortMetric = sortBy;

		const graphQLParams = {
			...this.queryParams,
			...additionalParams,
		};

		const graphqlQueryString = queryBuilder.getQuery(graphQLParams);

		// GraphQL query
		const query = gql`
			${graphqlQueryString}
		`;

		this.gridOptions?.api?.hideOverlay();

		// Set Total row with empty values while top level data is being requested by API.
		if (!params?.parentNode?.data) {
			this.gridOptions?.api?.setPinnedTopRowData([{ name: 'Total' }]);
		}

		this.client
			.query({ query })
			.then(({ data }) => {
				const { rowData, totalRow } = transformGraphQLResponse(
					data.tableRows,
					this.queryParams?.channel,
					this.queryParams?.weekFilter,
					this.queryParams?.timePeriods,
					this.queryParams?.viewBy,
					this.queryParams?.breakdownBy,
					this.queryParams?.rootParam,
					this.queryParams?.viewByType
				);

				setTimeout(() => {
					// Set the data in table rows
					if (rowData?.length) {
						if (rowData[0]?.level === 'ITEM' || rowData[0]?.level === 'STORE' || rowData[0]?.level === 'ALL_LINK_NUMBER' || rowData[0]?.level === 'PRIME_ITEM_NUMBER') {
							rowData.unshift({
								fullWidth: true,
								totalRows: rowData?.length,
								hierarchyLevel: rowData[0]?.level,
								sort: sort,
							});
						}

						params.success({
							rowData,
							rowCount: rowData?.length,
						});

						// Set Total row in table, by this condition total will not update when the child level rows are populated.
						if (!params?.parentNode?.data) {
							this.gridOptions?.api?.setPinnedTopRowData([...totalRow]);
						}

						const allColumnIds = [];
						params.columnApi.getColumns()?.forEach((column) => {
							allColumnIds.push(column.getId());
						});
						// auto size columns with header resize
						params.columnApi.autoSizeColumns(allColumnIds, false);
					} else {
						// inform the grid request failed
						params.fail();

						// This will show an overlay on table
						this.gridOptions?.api?.showNoRowsOverlay();
					}
				}, 400);
			})
			.catch(() => {
				params.fail();
				this.gridOptions?.api?.showNoRowsOverlay();
			});
		if (params?.parentNode?.level !== -1) {
			analytics.trackEventInfo({
				type: tracker.EVENTS.CLICK,
				actionOn: 'table_row_expanded_level_' + params?.parentNode?.level,
			});
		}
	}
}

export default ServerSideDataSource;
